import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps, SortableCartelItemProps } from "../../types/screensTypes";
import { CartelAI } from "../../types/types";
import { useGlobalState } from "../../context/useGlobalState";
import {
    deleteProject,
    getProjectByTypes,
    deleteItemFromProject,
    updateProject,
    updateCartelItemsPositionInProjects,
} from "../../api";
import TextInput from "../../components/ui/TextInput";
import Dropdown from "../../components/common/Dropdown";
import Icon from "../../components/common/Icon";
import TextModal from "../../components/modals/ProjectTextModal";
import DeleteModal from "../../components/modals/DeleteModal";
import s from "../../components/common/ContentSurface.module.css";

const SortableCartelItem: React.FC<SortableCartelItemProps> = ({
    cartel,
    setIsModalOpen,
    t,
    removeCartelAI,
    projectId,
    setSelectedCartel,
}) => {
    const navigate = useNavigate();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: cartel.id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition || "transform 250ms ease",
    };
    const langCode = cartel.language || "";

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className={s.row}>
                <div className={s.rowLeft}>
                    <div {...listeners} className={s.dragHandle}>
                        <svg width="18" height="18" viewBox="0 0 15 15" fill="currentColor">
                            <path d="M5 2.5H10M5 5.5H10M5 8.5H10M5 11.5H10" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </div>
                    <div className={s.info}>
                        <div className={s.nameRow}>
                            <h4
                                className={s.name}
                                onClick={() => navigate(`/cartel-projects/${projectId}/update-item/${cartel.id}`)}
                            >
                                {cartel.name}
                            </h4>
                            <Icon
                                name="ic-pencil"
                                className={s.editIcon}
                                onClick={() => navigate(`/cartel-projects/${projectId}/update-item/${cartel.id}`)}
                            />
                        </div>
                        <p className={s.meta}>
                            {cartel.text
                                ? cartel.text.length > 100
                                    ? `${cartel.text.substring(0, 100)}...`
                                    : cartel.text
                                : t("noInformationAvailable")}
                        </p>
                    </div>
                </div>

                <div className={s.rowRight}>
                    {langCode && <span className={s.lang}>{langCode.toUpperCase()}</span>}
                    {cartel.text && (
                        <Icon
                            name="ic-file-text"
                            className={s.iconAction}
                            onClick={() => {
                                setSelectedCartel(cartel);
                                setIsModalOpen(true);
                            }}
                        />
                    )}
                    <button type="button" className={s.removeBtn} onClick={async () => await removeCartelAI(cartel.id)}>
                        {t("remove")}
                    </button>
                </div>
            </div>
        </div>
    );
};

const CartelProjects: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { id } = useParams<{ id: string }>();
    const [projectData, setProjectData] = useState<any | null>(null);
    const [, setLoading] = useState<boolean>(true);
    const [, setError] = useState<string | null>(null);
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const [selectedCartel, setSelectedCartel] = useState<CartelAI | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { data } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [editedProjectName, setEditedProjectName] = useState<string>("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [search, setSearch] = useState<string>("");

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const fetchProject = async (projectId: string) => {
        if (!projectId) {
            setError("Something is wrong");
            setTimeout(() => setError(""), 3000);
            return;
        }
        setLoading(true);
        try {
            if (!token) return;
            const response = await getProjectByTypes({ token, projectId, targetTypes: ["cartel"] });
            setProjectData(response.project);
        } catch (error) {
            console.error("Error fetching project:", error);
            setError(t("errorFetchingProject"));
        }
        setLoading(false);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!active || !over || active.id === over.id) return;

        const currentList = sortedProjectData || [];
        const oldIndex = currentList.findIndex((item: any) => item.id === active.id);
        const newIndex = currentList.findIndex((item: any) => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const newFilteredOrder = arrayMove(currentList, oldIndex, newIndex);
        const newCartelAI = projectData!.cartelAI.map((item: any) => {
            const filteredIndex = newFilteredOrder.findIndex((filteredItem: any) => filteredItem.id === item.id);
            if (filteredIndex !== -1) return { ...item, position: filteredIndex + 1 };
            return item;
        });
        setProjectData({ ...projectData!, cartelAI: newCartelAI });

        try {
            const positionUpdates = newFilteredOrder.map((item: any, index) => ({ id: item.id, position: index + 1 }));
            if (!token) return;
            await updateCartelItemsPositionInProjects({ token, modifiedData: positionUpdates });
        } catch (error) {
            console.error("Failed to update positions:", error);
            fetchProject(id!);
        }
    };

    const removeCartelAI = async (cartelAIId: string) => {
        try {
            if (!token || !id) return;
            await deleteItemFromProject({ token, projectId: id, itemId: cartelAIId, itemType: "cartel" });
            await fetchProject(id);
        } catch (error) {
            console.error("Failed to remove item:", error);
        }
    };

    const refreshProject = async () => {
        if (id) await fetchProject(id);
    };

    const deleteProjectAI = async (projectId: string) => {
        try {
            if (!token) return;
            await deleteProject({ token, projectId });
            refreshProjects();
            navigate("/create/cartel");
        } catch (error) {
            console.error("Failed to delete project:", error);
        }
    };

    const handleDeleteConfirm = () => {
        if (id) deleteProjectAI(id);
    };

    const searchLc = search.trim().toLowerCase();
    const filteredProjectData = (projectData?.cartelAI || [])
        .filter((p: any) => (selectedContentLanguage ? p.language === selectedContentLanguage : true))
        .filter((p: any) => (searchLc ? (p.name || "").toLowerCase().includes(searchLc) : true));
    const sortedProjectData = [...filteredProjectData].sort((a: any, b: any) => a.position - b.position);

    const handleTitleChange = async () => {
        try {
            if (!token || !id) return;
            await updateProject({ token, projectId: id, name: editedProjectName });
            setProjectData((prev: any) => (prev ? { ...prev, name: editedProjectName } : prev));
            setIsEditingTitle(false);
            await refreshProject();
            refreshProjects();
        } catch (error) {
            console.error("Failed to update project name:", error);
            setIsEditingTitle(false);
            setEditedProjectName(projectData?.name || "");
        }
    };

    useEffect(() => {
        if (id) fetchProject(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleLanguageChange = (language: string | null) => {
        setSelectedContentLanguage(language);
        localStorage.setItem("selectedLanguageFilter", language || "");
    };

    useEffect(() => {
        const storedLanguage = localStorage.getItem("selectedLanguageFilter");
        if (storedLanguage) setSelectedContentLanguage(storedLanguage);
    }, []);

    return (
        <div className={s.page}>
            <div className={s.headerTop}>
                <h1 className={s.title}>{t("myCartelProjects")}</h1>
                <p className={s.subtitle}>{t("viewAndManageYourCartelCreation")}</p>
            </div>

            <div className={s.projectBar}>
                <div className={s.projectBarLeft}>
                    {isEditingTitle ? (
                        <TextInput
                            placeholder={t("nameOfTheNewProject")}
                            value={editedProjectName}
                            size="small"
                            onChange={(e) => setEditedProjectName(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && handleTitleChange()}
                        />
                    ) : (
                        <>
                            <h2 className={s.projectName}>
                                {projectData?.name} ({projectData?.cartelAI?.length || 0})
                            </h2>
                            <Icon
                                name="ic-pencil"
                                className={s.editIcon}
                                onClick={() => {
                                    setEditedProjectName(projectData?.name || "");
                                    setIsEditingTitle(true);
                                }}
                            />
                            <button
                                type="button"
                                className={s.addLink}
                                onClick={() => navigate(`/cartel-projects/${id}/add-item`)}
                            >
                                {t("add")}
                            </button>
                        </>
                    )}
                </div>
                <button type="button" className={s.dangerBtn} onClick={() => setIsDeleteModalOpen(true)}>
                    {t("removeProject")}
                </button>
            </div>

            <div className={s.divider} />

            <div className={s.toolbar}>
                <div className={s.search}>
                    <Icon name="ic-search" className={s.searchIcon} />
                    <input
                        className={s.searchInput}
                        type="text"
                        placeholder={t("searchByTitleVoiceLanguage")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <Dropdown
                        label=""
                        value={selectedContentLanguage}
                        options={data?.languagesAI?.map((l: { code: string; label: string }) => ({
                            value: l.code,
                            label: l.label,
                        }))}
                        onChange={handleLanguageChange}
                        placeholder={t("selectALanguage")}
                        loading={false}
                        error={false}
                    />
                </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortedProjectData?.map((a: any) => a.id) || []}>
                    <div className={s.list}>
                        {sortedProjectData?.map((cartel: any) => (
                            <SortableCartelItem
                                key={cartel.id}
                                cartel={cartel}
                                setIsModalOpen={setIsModalOpen}
                                t={t}
                                removeCartelAI={removeCartelAI}
                                refreshProject={refreshProject}
                                projectId={id!}
                                selectedCartel={selectedCartel}
                                setSelectedCartel={setSelectedCartel}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            {isModalOpen && selectedCartel && (
                <TextModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedCartel.name}
                    content={selectedCartel.text || ""}
                />
            )}

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                message={t("deleteProjectConfirmation")}
                title={t("deleteProject")}
            />
        </div>
    );
};

export default CartelProjects;
